/**
 * Apple Pay Button Component
 * Uses Coinflow's React SDK component for Apple Pay checkout
 * 
 * IMPORTANT: This component pre-creates a payment transaction before rendering
 * the Coinflow button. The transactionId is passed in webhookInfo so the backend
 * webhook handler can find and settle the correct transaction.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { CoinflowApplePayButton, Currency } from '@coinflowlabs/react';
import { apiClient } from '@/lib/api-client';
import { getDeviceId } from '@/lib/nsure';
import { COINFLOW_PRODUCT_TYPE, COINFLOW_PRODUCT_DESCRIPTION } from '@/lib/coinflow-tokenization';

interface ApplePayButtonProps {
  amount: number; // in cents
  packageId: string;
  scheduledOfferId?: string;  // For scheduled offer purchases
  packageTitle: string;
  merchantId: string;
  onSuccess: (paymentId: string, data: { isFirstPurchase?: boolean }) => void;
  onError: (error: string | Error) => void;
  /** Checkout session transaction ID - if provided, uses this instead of creating a new transaction */
  checkoutSessionId?: number | null;
}

export const ApplePayButton: React.FC<ApplePayButtonProps> = ({
  amount,
  packageId,
  scheduledOfferId,
  packageTitle,
  merchantId,
  onSuccess,
  onError,
  checkoutSessionId,
}) => {
  const [sessionKey, setSessionKey] = useState<string | null>(null);
  const [transactionId, setTransactionId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFirstPurchase, setIsFirstPurchase] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const hasFetchedRef = useRef(false);
  const paymentCompletedRef = useRef(false); // Track if payment completed successfully
  const paymentActivatedRef = useRef(false); // Track if user clicked the pay button
  const ownsTransaction = useRef(false); // Track if this component created the transaction

  // Initialize: use checkout session or create own transaction, then get session key
  useEffect(() => {
    // Prevent duplicate fetches
    if (hasFetchedRef.current || !merchantId) return;
    
    const initialize = async () => {
      try {
        hasFetchedRef.current = true;
        setIsLoading(true);
        setError(null);

        // Step 1: Use checkout session transaction or create our own
        if (checkoutSessionId) {
          // Use shared checkout session transaction
          setTransactionId(checkoutSessionId);
          ownsTransaction.current = false;
        } else {
          // Fallback: Create our own transaction (legacy behavior)
          // NOTE: This should rarely happen as PaymentCheckoutModal now waits for checkoutSessionId
          const initResponse = await apiClient.post('/payments/initiate-wallet-payment', {
            ...(scheduledOfferId ? { scheduledOfferId } : { packageId }),
            paymentMethod: 'apple_pay',
            amount,
          });
          setTransactionId(initResponse.transactionId);
          ownsTransaction.current = true;
        }

        // Step 2: Get session key for Coinflow SDK
        const sessionResponse = await apiClient.get('/payments/tokenize/session-key');
        setSessionKey(sessionResponse.sessionKey);
        
        // Step 3: Check if this will be user's first purchase (for Meta Pixel tracking)
        try {
          const profileResponse = await apiClient.get('/profile');
          const profile = profileResponse.profile || profileResponse;
          setIsFirstPurchase(!profile.first_purchase_at);
        } catch {
          // Non-critical - default to false if profile fetch fails
        }
      } catch (err: any) {
        hasFetchedRef.current = false; // Allow retry on error
        const errorMessage = err.response?.data?.message || err.message || 'Failed to initialize Apple Pay';
        setError(errorMessage);
        // Only call onError if it's a new error (not a retry)
        if (!error) {
          onError(new Error(errorMessage));
        }
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, packageId, scheduledOfferId, amount, checkoutSessionId]); // Include checkoutSessionId as dep

  // Determine environment - memoized to prevent re-renders
  const env = useMemo(() => 
    import.meta.env.VITE_COINFLOW_ENVIRONMENT === 'production' ? 'prod' : 'sandbox',
    []
  );

  // Handle height change (required prop) - memoized to prevent re-renders
  const handleHeightChange = useCallback((height: string) => {
    // Keep height fixed at 40px to prevent it from growing too tall
    if (wrapperRef.current) {
      wrapperRef.current.style.height = '40px';
      wrapperRef.current.style.maxHeight = '40px';
    }
  }, []);

  // Handle success callback - memoized to prevent re-renders
  const handleSuccess = useCallback((result: { paymentId: string; hash?: string } | string) => {
    paymentCompletedRef.current = true;
    const paymentId = typeof result === 'string' ? result : result.paymentId;
    onSuccess(paymentId, { isFirstPurchase });
  }, [onSuccess, isFirstPurchase]);

  // Activate the transaction when user clicks the Apple Pay button
  // This changes status from 'on_checkout' to 'awaiting_payment' and sets payment_method to 'apple_pay'
  const handleWrapperClick = useCallback(async () => {
    if (!transactionId || paymentActivatedRef.current) return;
    
    paymentActivatedRef.current = true;
    try {
      await apiClient.post('/payments/activate-checkout', { 
        transactionId,
        paymentMethod: 'apple_pay',
      });
    } catch {
      // Don't block payment if activation fails - webhook will still work
    }
  }, [transactionId]);

  // Cleanup on unmount - abandon transaction only if we created it and payment not completed
  useEffect(() => {
    return () => {
      // Only abandon if we created this transaction ourselves (not using shared checkout session)
      if (transactionId && !paymentCompletedRef.current && ownsTransaction.current) {
        // Fire and forget - don't await
        apiClient.post('/payments/abandon-checkout', { transactionId }).catch(() => {});
      }
    };
  }, [transactionId]);

  // Handle error callback - memoized to prevent re-renders
  const handleError = useCallback((message: string) => {
    onError(new Error(message));
  }, [onError]);

  // Memoize chargeback protection data to prevent re-renders
  const chargebackProtectionData = useMemo(() => [
    {
      productName: 'package',
      productType: COINFLOW_PRODUCT_TYPE,
      quantity: 1,
      rawProductData: {
        description: COINFLOW_PRODUCT_DESCRIPTION,
        packageId,
        packageTitle,
        amount,
      },
    },
  ], [packageId, packageTitle, amount]);

  // Memoize webhook info to prevent re-renders
  // CRITICAL: Include transactionId so the webhook can find the pre-created transaction
  const webhookInfo = useMemo(() => ({
    transactionId: transactionId?.toString(),
    // Keep packageId/scheduledOfferId as backup identification
    packageId: scheduledOfferId ? undefined : packageId,
    scheduledOfferId,
  }), [transactionId, packageId, scheduledOfferId]);

  // Memoize device ID to prevent re-renders
  const deviceId = useMemo(() => getDeviceId(), []);

  // Clean up extra divs after render - only run once when both sessionKey and transactionId are set
  // IMPORTANT: This must be before early returns to follow Rules of Hooks
  useEffect(() => {
    if (!sessionKey || !transactionId || !wrapperRef.current) return;

    let hasCleanedUp = false;

    // Remove any duplicate Apple Pay buttons (Coinflow might render multiple)
    const cleanupDuplicates = () => {
      if (!wrapperRef.current || hasCleanedUp) return;
      
      // Hide empty divs
      const emptyDivs = wrapperRef.current.querySelectorAll('div[style*="height: 0px"], div[style*="height:0px"]');
      emptyDivs.forEach((div) => {
        (div as HTMLElement).style.display = 'none';
      });

      // Remove overlay elements with high z-index that cover the button
      const overlays = wrapperRef.current.querySelectorAll('div[style*="z-index: 20"], div[style*="z-index:20"]');
      overlays.forEach((div) => {
        (div as HTMLElement).remove();
      });

      // Remove any absolute positioned overlays with z-index
      const allDivs = wrapperRef.current.querySelectorAll('div[style*="position: absolute"]');
      allDivs.forEach((div) => {
        const style = (div as HTMLElement).style.cssText;
        if (style.includes('z-index') && (style.includes('z-index: 20') || style.includes('z-index:20'))) {
          (div as HTMLElement).remove();
        }
      });

      hasCleanedUp = true;
    };

    // Run cleanup once after a short delay
    const timeout = setTimeout(cleanupDuplicates, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [sessionKey, transactionId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="text-sm text-muted-foreground">Loading Apple Pay...</div>
      </div>
    );
  }

  // Require both sessionKey AND transactionId before rendering
  // transactionId is needed for the webhook to find the pre-created transaction
  if (error || !sessionKey || !transactionId) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="text-sm text-destructive">
          {error || 'Failed to initialize Apple Pay'}
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={wrapperRef} 
      className="apple-pay-wrapper" 
      style={{ display: 'block', lineHeight: 0, height: '40px', maxHeight: '40px', overflow: 'hidden', width: '100%', pointerEvents: 'auto' }}
      onClick={handleWrapperClick}
      onTouchStart={handleWrapperClick}
    >
      <CoinflowApplePayButton
        key={`applepay-${sessionKey}-${transactionId}`}
        env={env}
        sessionKey={sessionKey}
        merchantId={merchantId}
        handleHeightChange={handleHeightChange}
        subtotal={{
          cents: amount,
          currency: Currency.USD,
        }}
        color="white"
        onSuccess={handleSuccess}
        onError={handleError}
        chargebackProtectionData={chargebackProtectionData}
        deviceId={deviceId}
        webhookInfo={webhookInfo}
        origins={[window.location.origin]}
      />
    </div>
  );
};

